/* eslint-disable */
import LayerStyle from "./LayerStyle";
import GeometryTypeMapLibre from "./GeometryTypeMapLibre";
import LayerConfig from "./LayerConfig";

export default class LayerStyleMapLibre {
    style: LayerStyle;
    type: string;
    complexStyle: any;
    hasComplexStyle: boolean;
    currentComplexStyle: any
    layer: LayerConfig

    aRemoveComplexPaintProperty = [
        'fill-opacity',
        'circle-opacity'
    ];

    constructor(style: LayerStyle, type: string, layer: LayerConfig) {
        this.style = style;
        this.type = type;
        this.complexStyle = layer.complexStyle;
        this.hasComplexStyle = this.initHasComplexStyle(layer.complexStyle);
        this.layer = layer;
    }

    getComplexStyle() {
        return this.complexStyle.toJson();
    }

    hasStyle() {
        return [GeometryTypeMapLibre.LINE, GeometryTypeMapLibre.POINT, GeometryTypeMapLibre.POLYGON].includes(this.type);
    }

    initHasComplexStyle(complexStyle: any) {
        if (! complexStyle) return false;

        try {
            const oComplexStyle = complexStyle.toJson();
            return (Array.isArray(oComplexStyle.styleRules) && oComplexStyle.styleRules.length > 0);
        } catch (error) {
            return false;
        }
    }

    toMapLibre() {
        if (! this.hasStyle()) {
            return false
        }

        let oStyle:any = {};

        switch (this.type) {
            case GeometryTypeMapLibre.POLYGON:
                oStyle = this.getFillStyleMapLibre();
                oStyle.others = this.getFillLineStyleMapLibre();
                break;
            case GeometryTypeMapLibre.POINT:
                oStyle = this.getCircleStyleMapLibre();
                break;
            case GeometryTypeMapLibre.LINE:
                oStyle = this.getLineStyleMapLibre();
                break;
        }

        oStyle.etiquette = this.getLayoutEtiquette();

        return oStyle
    }

    getUsedStyle() {
        return this.hasComplexStyle ? this.currentComplexStyle : this.style;
    }

    getFillStyle(bInitial=false) {
        const oStyle = (bInitial) ? this.style : this.getUsedStyle();
        return oStyle.fillStyle;
    }

    getStrokeStyle(bInitial=false) {
        const oStyle = (bInitial) ? this.style : this.getUsedStyle();
        return oStyle.strokeStyle;
    }

    getTextStyle() {
        return this.style.textStyle;
    }

    getPointSymbolStyle(bInitial=false) {
        const oStyle = (bInitial) ? this.style : this.getUsedStyle();
        return oStyle.pointSymbol;
    }

    strokeStyleSimpleToMapLibre(params: any) {
        const oStrokeStyle = this.getStrokeStyle();
        const strokeWidth = oStrokeStyle.strokeWidth ?? 1;
        return {
            [params.color] : oStrokeStyle.color,
            [params.width] : parseInt(strokeWidth.toString())
        }
    }

    isSimpleStrokeStyle() {
        const oStrokeStyle = this.getStrokeStyle();
        return oStrokeStyle.pattern === 'simple' && (oStrokeStyle.strokeWidth === 1 || oStrokeStyle.strokeWidth === undefined);
    }

    fillStyleSimpleToMapLibre() {
        const oStyle: any = this.fillStyleSimpleColorToMapLibre();
        const oStrokeStyle = this.getStrokeStyle();

        if (! this.isSimpleStrokeStyle()) {
            return oStyle;
        }
        oStyle['fill-outline-color'] = oStrokeStyle.color;
        return oStyle;
    }

    fillStyleSimpleColorToMapLibre() {
        const oFillStyle = this.getFillStyle();
        return this.getColorMapLibre(oFillStyle.fillColor, {
            color: 'fill-color',
            opacity: 'fill-opacity'
        })
    }

    getColorMapLibre (color: string, params: any) {
        if (! this.isColorRgba(color)) {
            return {
                [params.color]: color
            }
        }

        return this.getColorByRgba(color, params);
    }

    isColorRgba(color: string) {
        return color.startsWith('rgba');
    }

    getColorByRgba(color: string, params: any) {
        const pattern = /rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(0|0?\.\d+|1(\.0)?)\s*\)/;
        const matches = color.match(pattern);
        if (! matches) {
            return {};
        }
        const opacity = (matches[4] !== undefined) ? parseFloat(matches[4]) : 1;
        return {
            [params.color] : `rgb(${matches[1]}, ${matches[2]}, ${matches[3]})`,
            [params.opacity] : opacity,
        }
    }

    getFillStyleMapLibre() {
        let style = {};
        if (this.hasComplexStyle) {
            style = this.complexStyleToMapLibre(this.fillStyleSimpleToMapLibre.bind(this), 'fill');
        } else {
            style = this.fillStyleSimpleToMapLibre();
        }

        return style;
    }

    getFillLineStyleMapLibre() {
        if (this.isSimpleStrokeStyle() || this.hasComplexStyle) {
            return false;
        }

        return this.strokeStyleSimpleToMapLibre({
            color: 'line-color',
            width: 'line-width',
        })
    }

    circleStyleSimpleToMapLibre() {
        const oStyle: any = this.getColorMapLibre(this.getSymbolFillColor(), {
            color: 'circle-color',
            opacity: 'circle-opacity',
        })

        oStyle['circle-stroke-color'] = this.getSymbolStrokeColor();
        oStyle['circle-stroke-width'] = this.getSymbolStrokeWidth();
        oStyle['circle-radius'] = this.getCircleRadius();

        return oStyle;
    }

    getValueCircleProperty(property: string, defaultValue: any) {
        const oPointSymboleStyle = this.getPointSymbolStyle();
        const oPointSymboleStyleInitial = this.getPointSymbolStyle(true);
        return oPointSymboleStyle[property] ?? oPointSymboleStyleInitial[property] ?? defaultValue;
    }

    getSymbolStrokeWidth() {
        return parseInt(this.getValueCircleProperty('symbolStrokeWidth', 1));
    }

    getCircleRadius() {
        return parseInt(this.getValueCircleProperty('size', 10));
    }

    getSymbolStrokeColor() {
        return this.getValueCircleProperty('symbolStrokeColor', '#ff0000');
    }

    getSymbolFillColor() {
        return this.getValueCircleProperty('symbolFillColor', '#ff0000');
    }

    getCircleStyleMapLibre() {
        let style = {};
        if (this.hasComplexStyle) {
            style = this.complexStyleToMapLibre(this.circleStyleSimpleToMapLibre.bind(this), 'circle');
        } else {
            style = this.circleStyleSimpleToMapLibre();
        }

        return style;
    }

    lineStyleSimpleToMapLibre() {
        return this.strokeStyleSimpleToMapLibre({
            color: 'line-color',
            width: 'line-width',
        })
    }

    getLineStyleMapLibre() {
        let style = {};
        if (this.hasComplexStyle) {
            style = this.complexStyleToMapLibre(this.lineStyleSimpleToMapLibre.bind(this), 'line');
        } else {
            style = this.lineStyleSimpleToMapLibre();
        }

        return style;
    }

    ruleToMaplibre(rule: string) {
        const oRule = new Rule(rule, this.getLayerProperties())
        return oRule.toMapLibre();
    }

    complexStyleToMapLibre(fnStyleToMapLibre: any, type: string) {
        const { styleRules } = this.getComplexStyle();
        if( !(Array.isArray(styleRules) && styleRules.length)) {
            return {};
        }

        const aComplexProperty = this.getComplexStyleProperty(styleRules, type);
        if (! aComplexProperty.length) {
            return {};
        }

        const oStyle: any = {};

        this.currentComplexStyle = this.style;
        const oDefaultMapLibreStyle = fnStyleToMapLibre();

        styleRules.forEach((oStyleRule:any, index) => {
            this.currentComplexStyle = oStyleRule.style;
            const oMapLibreStyle = fnStyleToMapLibre();

            for (const property of Object.keys(oMapLibreStyle)) {
                if(! aComplexProperty.includes(property)) {
                    continue;
                }

                const filter = this.ruleToMaplibre(oStyleRule.value);
                if (! filter) {
                    continue;
                }

                if( oStyle.hasOwnProperty(property)) {
                    oStyle[property].push(filter);
                    oStyle[property].push(oMapLibreStyle[property]);
                } else {
                    oStyle[property] = ['case', filter, oMapLibreStyle[property]];
                }

                if (index === styleRules.length - 1) {
                    oStyle[property].push(oDefaultMapLibreStyle[property] ?? oStyle[property].at(-1))
                }
            }
        });

        return this.removeComplexPaintProperty({...oDefaultMapLibreStyle, ...oStyle});
    }

    removeComplexPaintProperty(oStyle: any)  {
        const oComplexStyle: any = {};
        for (const [key, value] of Object.entries(oStyle)) {
            if (this.aRemoveComplexPaintProperty.includes(key)) {
                continue;
            }

            oComplexStyle[key] = value;
        }
        return oComplexStyle;
    }

    getLayerProperties() {
        return this.layer.layerProperties;
    }

    castClassificationValue(property: string, value: any) {
        for(const oLayerProperty of this.getLayerProperties()) {
            if (oLayerProperty.name !== property) {
                continue;
            }

            switch (oLayerProperty.type) {
                case 'xsd:boolean':
                    return Boolean(value);
                case 'xsd:double':
                    return parseFloat(value);
                case 'xsd:int':
                    return parseInt(value);
                default:
                    return value.toString();
            }
        }
        return value.toString();
    }

    getLayoutEtiquette() {
        const oTextStyle = this.getTextStyle();
        if (! oTextStyle?.text) {
            return false;
        }

        return {
            layout: {
                'text-field': oTextStyle.text,
                'text-font': ['Source Sans Pro Regular'],
                'text-offset': [oTextStyle.offsetX, oTextStyle.offsetY],
                'text-size': oTextStyle.textFontSize
            },
            paint: {
                'text-color': oTextStyle.textFillColor,
            }
        }
    }

    getComplexStyleProperty(stylesRules: any, type: any) {
        if (type === 'fill') {
            return ['fill-color', 'fill-outline-color'];
        } else if (type === 'line') {
            return ['line-color'];
        }

        const aTypes: any = [];

        for (const oRules of stylesRules) {
            const pointSymbol = oRules.style.pointSymbol;

            if (pointSymbol?.symbolFillColor && ! aTypes.includes('circle-color')) {
                aTypes.push('circle-color');
                aTypes.push('circle-stroke-color');
            } else if (pointSymbol?.size && ! aTypes.includes('circle-radius')) {
                aTypes.push('circle-radius');
            }

            if (this.isInArray(['circle-color', 'circle-stroke-color', 'circle-radius'], aTypes)) {
                break;
            }
        }

        return aTypes;
    }

    isInArray(aMain: any[], aCheck: any[]) {
        return aCheck.every(element => aMain.includes(element));
    }
}

class Rule {

    rule: string;
    layerProperties: any;
    oMapLibreConditions: any;
    index: number;

    regexConditionInBracket = /\([^\(\)]+\)/;
    regexComparisonOperator = /[=<>]{1,2}/;
    regexKey = /\[[a-z]{1}\]/;

    characters = 'abcdefghijklmnopqrstuvwxyz';

    constructor(rule: string, layerProperties: any) {
        this.rule = rule.toString().toLowerCase();
        this.layerProperties = layerProperties;
        this.oMapLibreConditions = {};
        this.index = 0;
    }

    toMapLibre() {
        this.replaceConditionInBracket();

        return this.containsLogicalOperator(this.rule) ?
            this.castConditionWithLogicalOperatorToMapLibre(this.rule) : this.castConditionToMapLibre(this.rule);
    }

    replaceConditionInBracket() {
        const matches = this.rule.match(this.regexConditionInBracket);
        if(! matches) {
            return
        }

        for (const condition of matches) {
            const tmpCondition = condition.substring(1, condition.length - 1);
            const mapLibreCondition = this.containsLogicalOperator(tmpCondition) ?
                this.castConditionWithLogicalOperatorToMapLibre(tmpCondition) : this.castConditionToMapLibre(tmpCondition)

            if (! mapLibreCondition) {
                continue;
            }

            const key = this.characters[this.index];
            this.oMapLibreConditions[key!] = mapLibreCondition;
            this.rule = this.rule.replace(condition, `[${key}]`)
        }

        const rematches = this.rule.match(this.regexConditionInBracket);
        if(rematches) {
            this.replaceConditionInBracket();
            return;
        }

        return;
    }

    containsLogicalOperator(condition: string) {
        return condition.includes(' or ') || condition.includes(' and ');
    }

    castConditionWithLogicalOperatorToMapLibre(condition: string) {
        const bContainsLogicalOperatorAnd = condition.includes(' and ');
        let aCondition: any = condition.split(bContainsLogicalOperatorAnd ? ' and ' : ' or ');

        aCondition.forEach((cd: string, i: number) => {
            aCondition[i] = this.castConditionToMapLibre(cd);
        });

        if (aCondition.includes(false)) {
            return false;
        }

        return [
            bContainsLogicalOperatorAnd ? 'all' : 'any',
            ...aCondition
        ];
    }

    castConditionToMapLibre(condition: string) {
        const keyMatches = condition.match(this.regexKey);
        if(keyMatches?.length) {
            return this.getMapLibreConditionByKeys(keyMatches[0]);
        }

        const matches = condition.match(this.regexComparisonOperator);
        if (! matches) {
            return false;
        }

        let operator = matches[0];
        const aValues = condition.split(operator);
        if (aValues.length !== 2) {
            return false;
        }

        if (operator === '=') {
            operator = '==';
        }

        const property = aValues[0].trim();
        const value = aValues[1].trim().replace(/^'|'$/g, '');
        return [operator, ...this.castValue(property, value)];
    }

    castValue(property: string, value: any) {
        const aGetProperty = ['get', property];

        for(const oLayerProperty of this.layerProperties) {
            if (oLayerProperty.name !== property) {
                continue;
            }

            switch (oLayerProperty.type) {
                case 'xsd:boolean':
                    return [aGetProperty, Boolean(value)];
                case 'xsd:double':
                    return [aGetProperty, parseFloat(value)];
                case 'xsd:int':
                    return [aGetProperty, parseInt(value)];
                default:
                    return [['downcase', aGetProperty], value.toString()];
            }
        }
        return [['downcase', aGetProperty], value.toString()];
    }

    getMapLibreConditionByKeys(key: string) {
        return this.oMapLibreConditions[key.substring(1, key.length - 1)];
    }
 }